
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { AppSettingsService } from '@/services/app-settings-service';
import { ExternalLink, Save, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export const AiPropSettingsManagement = () => {
  const [defaultLink, setDefaultLink] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidUrl, setIsValidUrl] = useState(true);

  // Load current setting
  useEffect(() => {
    const loadCurrentSetting = async () => {
      try {
        setIsLoading(true);
        const currentLink = await AppSettingsService.getAiPropDefaultLink();
        setDefaultLink(currentLink);
      } catch (error) {
        console.error('Error loading AI Prop default link:', error);
        toast.error('Failed to load current setting');
      } finally {
        setIsLoading(false);
      }
    };

    loadCurrentSetting();
  }, []);

  // Validate URL format
  const validateUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleLinkChange = (value: string) => {
    setDefaultLink(value);
    setIsValidUrl(value === '' || validateUrl(value));
  };

  const handleSave = async () => {
    if (!defaultLink.trim()) {
      toast.error('Please enter a default link');
      return;
    }

    if (!isValidUrl) {
      toast.error('Please enter a valid URL');
      return;
    }

    try {
      setIsSaving(true);
      const success = await AppSettingsService.updateAiPropDefaultLink(defaultLink.trim());
      
      if (success) {
        toast.success('AI Prop default link updated successfully');
      } else {
        toast.error('Failed to update AI Prop default link');
      }
    } catch (error) {
      console.error('Error updating AI Prop default link:', error);
      toast.error('An error occurred while updating the setting');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestLink = () => {
    if (defaultLink && isValidUrl) {
      window.open(defaultLink, '_blank', 'noopener,noreferrer');
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 animate-spin" />
            Loading AI Prop Settings...
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>AI Prop Default Link Management</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertDescription>
            This link will be used as the default AI Prop referral link for users who don't have an affiliate referral.
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <Label htmlFor="ai-prop-link">Default AI Prop Link</Label>
          <div className="flex gap-2">
            <Input
              id="ai-prop-link"
              type="url"
              value={defaultLink}
              onChange={(e) => handleLinkChange(e.target.value)}
              placeholder="https://aiprop.com/your-default-ref"
              className={!isValidUrl ? 'border-red-500' : ''}
            />
            <Button
              variant="outline"
              size="icon"
              onClick={handleTestLink}
              disabled={!defaultLink || !isValidUrl}
              title="Test link"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
          {!isValidUrl && (
            <p className="text-sm text-red-600">Please enter a valid URL</p>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleSave}
            disabled={isSaving || !defaultLink.trim() || !isValidUrl}
            className="flex items-center gap-2"
          >
            {isSaving ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
